import React, { useCallback, useContext, useMemo } from 'react';

import Collapse from '@mui/material/Collapse';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import ScreenContext from 'client/app/components/AppRouter/ScreenContext';
import { useContentsOfLayer } from 'client/app/components/Parameters/PlateLayout/lib/useContentsOfLayer';
import { MainPanelTab } from 'client/app/components/Parameters/PlateLayout/PlateLayoutEditorContents';
import { usePlateLayoutEditorContext } from 'client/app/components/Parameters/PlateLayout/PlateLayoutEditorProvider';
import WellSelector, {
  WellSelectionProps,
} from 'client/app/components/WellSelector/WellSelector';
import { WellContents, WellLocationOnDeckItem } from 'common/types/mix';
import { PlateAssignmentMode } from 'common/types/plateAssignments';
import Colors from 'common/ui/Colors';
import { getLayoutForWellSelector } from 'common/ui/components/simulation-details/mix/DeckLayout';
import { PlateState } from 'common/ui/components/simulation-details/mix/MixState';
import { WellLabelContent } from 'common/ui/components/simulation-details/mix/WellLabel';
import makeWellSelector from 'common/ui/components/simulation-details/PlateTransform';
import Dropdown from 'common/ui/filaments/Dropdown';

export default function LayoutPlate() {
  const {
    plateType,
    plateLayers,
    focusedLayerId,
    liquidColors,
    isEditingCombinatorialWells,
    combinatorialPlateLayouts,
    selectedPlateName,
    plateAssignment,
  } = usePlateLayoutEditorContext();

  const activeLayerIndex = plateLayers.findIndex(layer => layer.id === focusedLayerId);

  const combinatorialLayers =
    plateAssignment.assignmentMode === PlateAssignmentMode.COMBINATORIAL &&
    selectedPlateName
      ? combinatorialPlateLayouts?.get(selectedPlateName)?.plateLayers
      : undefined;

  const [contents, contentsBelow] = useContentsOfLayer(
    combinatorialLayers ?? plateLayers,
    activeLayerIndex,
    liquidColors,
  );

  const plateState = useMemo<PlateState | undefined>(
    () =>
      plateType ? { ...makeWellSelector(plateType), contents, contentsBelow } : undefined,
    [contents, contentsBelow, plateType],
  );

  if (!plateState) {
    return (
      <NoPlateMessage>
        <Typography variant="h2">No plate type selected</Typography>
      </NoPlateMessage>
    );
  }

  return isEditingCombinatorialWells ? (
    <WellAvailabilityPlate plateState={plateState} />
  ) : (
    <LiquidAssignmentPlate plateState={plateState} activeLayerIndex={activeLayerIndex} />
  );
}

function WellAvailabilityPlate({ plateState }: { plateState: PlateState }) {
  const { screenId } = useContext(ScreenContext);
  const { liquidColors, selectedWells, setSelectedWells } = usePlateLayoutEditorContext();

  const deckLayout = useMemo(() => getLayoutForWellSelector(plateState), [plateState]);

  const getWellContentLabel = useCallback(
    (
      _well: string,
      _contents: WellContents | undefined,
      location: { row: number; col: number },
    ): WellLabelContent => {
      const selectedIndex = selectedWells.findIndex(
        selected => selected.col === location.col && selected.row === location.row,
      );
      const isIndexFound = selectedIndex !== undefined && selectedIndex > -1;
      return {
        type: 'ContentLabel',
        heading: isIndexFound ? `${selectedIndex + 1}` : '',
      };
    },
    [selectedWells],
  );

  const wellSelectionProps = useMemo<WellSelectionProps>(
    () => ({
      selectedWells,
      onSelectWells: value => setSelectedWells(value),
    }),
    [selectedWells, setSelectedWells],
  );

  return (
    <Wrapper>
      <WellSelector
        deckLayout={deckLayout}
        googleAnalyticsCategory={screenId ?? ''}
        liquidColors={liquidColors}
        focusedWells={selectedWells}
        plate={plateState}
        showContentLabels
        getContentLabel={getWellContentLabel}
        wellSelectionProps={wellSelectionProps}
      />
    </Wrapper>
  );
}

function LiquidAssignmentPlate({
  plateState,
  activeLayerIndex,
}: {
  plateState: PlateState;
  activeLayerIndex: number;
}) {
  const { screenId } = useContext(ScreenContext);
  const {
    setSelectedWells,
    liquidColors,
    plateAssignment,
    selectedWells,
    selectedLiquidOrLayerId,
    highlightedLiquidId,
    setHighlightedLiquidId,
    plateType,
    isReadonly,
    plateLayers,
  } = usePlateLayoutEditorContext();

  const selectedWellSet = useMemo(
    () =>
      activeLayerIndex > -1
        ? plateLayers[activeLayerIndex].wellSets.find(
            ws => ws.id === (highlightedLiquidId ?? selectedLiquidOrLayerId),
          )
        : undefined,
    [activeLayerIndex, highlightedLiquidId, plateLayers, selectedLiquidOrLayerId],
  );

  const getWellContentLabel = useCallback(
    (
      _well: string,
      _contents: WellContents | undefined,
      location: { row: number; col: number },
    ): WellLabelContent => {
      const selectedIndex = selectedWells.findIndex(
        well => well.col === location.col && well.row === location.row,
      );

      if (selectedIndex > -1) {
        return { type: 'ContentLabel', heading: `${selectedIndex + 1}` };
      }

      if (selectedWellSet) {
        const selectedWellSetIndex = selectedWellSet.wells.findIndex(
          well => well.x === location.col && well.y === location.row,
        );

        if (selectedWellSetIndex > -1) {
          return { type: 'ContentLabel', heading: `${selectedWellSetIndex + 1}` };
        }
      }

      return { type: 'ContentLabel', heading: '' };
    },
    [selectedWellSet, selectedWells],
  );

  const focusedWells = useMemo<readonly WellLocationOnDeckItem[] | undefined>(() => {
    return selectedWellSet
      ? [
          ...selectedWellSet.wells.map(({ x, y }) => ({
            col: x,
            row: y,
            deck_item_id: plateType?.id ?? '',
          })),
          ...selectedWells,
        ]
      : undefined;
  }, [plateType?.id, selectedWellSet, selectedWells]);

  const deckLayout = useMemo(() => getLayoutForWellSelector(plateState), [plateState]);

  const wellSelectionProps = useMemo<WellSelectionProps>(
    () =>
      plateAssignment.assignmentMode === PlateAssignmentMode.COMBINATORIAL
        ? {
            selectedWells: [],
            onSelectWells: () => {},
          }
        : {
            selectedWells,
            onSelectWells: isReadonly ? () => {} : value => setSelectedWells(value),
          },
    [isReadonly, plateAssignment.assignmentMode, selectedWells, setSelectedWells],
  );

  const onWellMouseEnter = useCallback(
    (loc: WellLocationOnDeckItem) => {
      if (selectedLiquidOrLayerId) {
        return;
      }

      const activeLayer = plateLayers[activeLayerIndex];
      const wellSet = activeLayer?.wellSets.find(ws =>
        ws.wells.some(well => well.x === loc.col && well.y === loc.row),
      );

      if (wellSet) {
        setHighlightedLiquidId(wellSet.id);
      }
    },
    [activeLayerIndex, plateLayers, selectedLiquidOrLayerId, setHighlightedLiquidId],
  );

  const onWellMouseLeave = useCallback(
    (loc: WellLocationOnDeckItem) => {
      if (selectedLiquidOrLayerId) {
        return;
      }

      const activeLayer = plateLayers[activeLayerIndex];
      const wellSet = activeLayer?.wellSets.find(ws =>
        ws.wells.some(well => well.x === loc.col && well.y === loc.row),
      );

      if (wellSet) {
        setHighlightedLiquidId(current => (current === wellSet.id ? undefined : current));
      }
    },
    [activeLayerIndex, plateLayers, selectedLiquidOrLayerId, setHighlightedLiquidId],
  );

  return (
    <Wrapper>
      <WellSelector
        deckLayout={deckLayout}
        googleAnalyticsCategory={screenId ?? ''}
        liquidColors={liquidColors}
        plate={plateState}
        wellSelectionProps={wellSelectionProps}
        getContentLabel={getWellContentLabel}
        showContentLabels
        onWellMouseEnter={onWellMouseEnter}
        onWellMouseLeave={onWellMouseLeave}
        focusedWells={focusedWells}
      />
    </Wrapper>
  );
}

type ActivePlateSelectorProps = {
  disabled?: boolean;
};

export function ActivePlateSelector(props: ActivePlateSelectorProps) {
  const { disabled } = props;
  const {
    selectedPlateName,
    setSelectedPlateName,
    plateOptions,
    isMixOnto,
    plateAssignment,
    activeMainTab,
  } = usePlateLayoutEditorContext();

  if (!selectedPlateName || !plateOptions) {
    return null;
  }

  // We only want to show the plate selector here if we are using a Mix Onto element
  // (so the user can view the different plates) or if we are in Combinatorial mode
  // (so the user can see the different generated plates). In Make Mixtures Descriptive
  // mode, we only ever want to show a single plate, therefore no plate selector.
  const hideActivePlateSelector =
    plateAssignment.assignmentMode !== PlateAssignmentMode.COMBINATORIAL &&
    !isMixOnto &&
    activeMainTab !== MainPanelTab.PREVIEW;

  const hasPlatesDefined = !!selectedPlateName && plateOptions?.length > 1;

  return (
    <StyledCollapse in={!hideActivePlateSelector && hasPlatesDefined}>
      <ActivePlateSelectorWrapper>
        <Typography variant="body2">{`${plateOptions.length} plates`}</Typography>
        <Dropdown
          placeholder="Plate name"
          valueLabel={selectedPlateName ?? ''}
          options={plateOptions}
          onChange={name => {
            name && setSelectedPlateName(name);
          }}
          isDisabled={disabled}
          isRequired
        />
      </ActivePlateSelectorWrapper>
    </StyledCollapse>
  );
}

const StyledCollapse = styled(Collapse)({
  gridArea: 'plateSelect',
});

const ActivePlateSelectorWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  marginTop: theme.spacing(6),
  gap: theme.spacing(4),
  '& .MuiFormControl-root': {
    maxWidth: '270px',
  },
}));

const Wrapper = styled('div')({
  gridArea: 'main',
});

const NoPlateMessage = styled('div')({
  display: 'grid',
  placeContent: 'center',
  background: Colors.GREY_10,
  color: Colors.TEXT_DISABLED,
  borderRadius: '4px',
});
